import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Send } from 'lucide-react';
import { sendEmail } from '../services/emailService';

const AwardForm = () => {
  const { categoryId } = useParams();
  const [activeSection, setActiveSection] = useState(1);
  const [formData, setFormData] = useState({
    // Section 1 - Basic Information
    institutionName: '',
    institutionType: '',
    ownershipType: '',
    website: '',
    yearEstablishment: '',
    address: '',
    contactPersonName: '',
    contactEmail: '',
    contactNumber: '',
    
    // Section 2 - About Institution
    instituteProfile: '',
    vcName: '',
    yearsAssociated: '',
    studentStrength: '',
    facultyStrength: '',
    nonTeachingStaff: '',
    accreditations: '',
    affiliatingBody: '',
    aiPartnerships: '',
    
    // Section 3 - Core Application
    visionGoals: '',
    aiTechnologies: '',
    aiInitiatives: '',
    facultyDevelopment: '',
    futureExpansion: '',
    whyRecognition: '',
    
    // Section 4 - Supporting Documents
    digitalAssets: '',
    
    // Declaration
    agreeCriteria: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailData = {
      name: formData.contactPersonName,
      email: formData.contactEmail,
      subject: 'Award Nomination Submission - Excellence in AI-Integrated Higher Education',
      message: formatNominationData(formData)
    };

    const result = await sendEmail(emailData, 'award-nomination');
    
    if (result.success) {
      alert('Nomination submitted successfully! We will review your application and contact you soon.');
      // Reset form or redirect
    } else {
      alert('Failed to submit nomination. Please try again or contact support.');
    }
  };

  const formatNominationData = (data) => {
    return `
AWARD NOMINATION SUBMISSION
===========================

Category: Excellence in AI-Integrated Higher Education

SECTION 1 - BASIC INFORMATION
-----------------------------
Institution Name: ${data.institutionName}
Institution Type: ${data.institutionType}
Ownership Type: ${data.ownershipType}
Website: ${data.website}
Year of Establishment: ${data.yearEstablishment}
Address: ${data.address}
Contact Person: ${data.contactPersonName}
Email: ${data.contactEmail}
Contact Number: ${data.contactNumber}

SECTION 2 - ABOUT THE INSTITUTION
----------------------------------
Institute Profile: ${data.instituteProfile}
VC/Head Name: ${data.vcName}
Years Associated: ${data.yearsAssociated}
Student Strength: ${data.studentStrength}
Faculty Strength: ${data.facultyStrength}
Non-Teaching Staff: ${data.nonTeachingStaff}
Accreditations: ${data.accreditations}
Affiliating Body: ${data.affiliatingBody}
AI Partnerships: ${data.aiPartnerships}

SECTION 3 - CORE APPLICATION QUESTIONS
---------------------------------------
Vision & Goals: ${data.visionGoals}
AI Technologies: ${data.aiTechnologies}
AI Initiatives: ${data.aiInitiatives}
Faculty Development: ${data.facultyDevelopment}
Future Expansion: ${data.futureExpansion}
Why Recognition: ${data.whyRecognition}

SECTION 4 - SUPPORTING DOCUMENTS
---------------------------------
Digital Assets: ${data.digitalAssets}

Submitted on: ${new Date().toLocaleString()}
    `;
  };

  const renderSection = () => {
    switch(activeSection) {
      case 1:
        return (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              SECTION 1 - BASIC INFORMATION
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Name of the Institution *
                </label>
                <input
                  type="text"
                  name="institutionName"
                  value={formData.institutionName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Type of Institution *
                </label>
                <select
                  name="institutionType"
                  value={formData.institutionType}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select Type</option>
                  <option value="University">University</option>
                  <option value="Deemed University">Deemed University</option>
                  <option value="Institute">Institute</option>
                  <option value="Autonomous College">Autonomous College</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Ownership Type *
                </label>
                <select
                  name="ownershipType"
                  value={formData.ownershipType}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select Ownership</option>
                  <option value="Public">Public</option>
                  <option value="Private">Private</option>
                  <option value="Deemed">Deemed</option>
                  <option value="Autonomous">Autonomous</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Website *
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Year of Establishment *
                </label>
                <input
                  type="number"
                  name="yearEstablishment"
                  value={formData.yearEstablishment}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Contact Person Name & Designation *
                </label>
                <input
                  type="text"
                  name="contactPersonName"
                  value={formData.contactPersonName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Email ID of the Contact Person *
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Contact Number of POC *
                </label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              SECTION 2 - ABOUT THE INSTITUTION
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Institute Profile * (Words limit - 500)
                </label>
                <textarea
                  name="instituteProfile"
                  value={formData.instituteProfile}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
                <p style={{ color: '#718096', fontSize: '0.8rem', marginTop: '5px' }}>
                  {formData.instituteProfile.length}/500 characters
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Name of the VC/ Head of Institution *
                </label>
                <input
                  type="text"
                  name="vcName"
                  value={formData.vcName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Number of Years Associated with the Institute *
                </label>
                <input
                  type="number"
                  name="yearsAssociated"
                  value={formData.yearsAssociated}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                    Total Student Strength *
                  </label>
                  <input
                    type="number"
                    name="studentStrength"
                    value={formData.studentStrength}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                    Total Faculty Strength *
                  </label>
                  <input
                    type="number"
                    name="facultyStrength"
                    value={formData.facultyStrength}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                    Total Non-Teaching Staff *
                  </label>
                  <input
                    type="number"
                    name="nonTeachingStaff"
                    value={formData.nonTeachingStaff}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Accreditations / Rankings (Words limit - 100)
                </label>
                <textarea
                  name="accreditations"
                  value={formData.accreditations}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Key AI-related Partnerships * (Words limit - 100)
                </label>
                <textarea
                  name="aiPartnerships"
                  value={formData.aiPartnerships}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              SECTION 3 - CORE APPLICATION QUESTIONS
            </h3>
            <div style={{ display: 'grid', gap: '25px' }}>
              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Describe your institution's vision and long-term goals for integrating AI * (Words limit - 2000)
                </label>
                <textarea
                  name="visionGoals"
                  value={formData.visionGoals}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={2000}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  List and describe AI technologies, tools, or platforms adopted * (Words limit - 2000)
                </label>
                <textarea
                  name="aiTechnologies"
                  value={formData.aiTechnologies}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={2000}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Why should your institution be recognized for this award? * (Words limit - 500)
                </label>
                <textarea
                  name="whyRecognition"
                  value={formData.whyRecognition}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  maxLength={500}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              SECTION 4 - SUPPORTING DOCUMENTS
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  Links to Digital Assets (Social Media, YouTube Videos etc) * (Words limit - 100)
                </label>
                <textarea
                  name="digitalAssets"
                  value={formData.digitalAssets}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <p style={{ color: '#718096', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Note: File upload functionality for videos, presentations, certificates, and other documents will be available in the full implementation.
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              DECLARATION
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="agreeCriteria"
                  checked={formData.agreeCriteria}
                  onChange={handleInputChange}
                  required
                  style={{ marginTop: '5px' }}
                />
                <label style={{ color: '#2d3748', fontSize: '1rem', lineHeight: '1.5' }}>
                  I have read and agree to the judging and evaluation criteria for nomination in this category. *
                </label>
              </div>

              <div style={{
                backgroundColor: '#f7fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #e2e8f0'
              }}>
                <p style={{ color: '#2d3748', fontSize: '1rem', fontWeight: 600, marginBottom: '10px' }}>
                  Self-Declaration:
                </p>
                <p style={{ color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  I hereby declare that the information provided in this application form is true, correct, and complete to the best of my knowledge and belief.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '40px 24px 0' }}>
        <div className="container-custom">
          <Link to="/awards/nomination/1" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#667eea', textDecoration: 'none', marginBottom: '30px', fontWeight: '600' }}>
            <ArrowLeft size={20} />
            <span>Back to Nomination</span>
          </Link>
          
          <div style={{ marginBottom: '40px' }}>
            <h1 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '10px' }}>
              Excellence in AI-Integrated Higher Education
            </h1>
            <p style={{ color: '#667eea', fontSize: '1.1rem', fontWeight: 600, marginBottom: '10px' }}>
              [Master Category: HIGHER EDUCATION CATEGORIES]
            </p>
            <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6' }}>
              Honors higher education institutions that have strategically integrated Artificial Intelligence (AI) across their academic or administrative, or in research ecosystems to enhance learning outcomes, operational efficiency, and innovation.
            </p>
          </div>

          {/* Section Navigation */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '40px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { key: 1, label: 'SECTION 1' },
              { key: 2, label: 'SECTION 2' },
              { key: 3, label: 'SECTION 3' },
              { key: 4, label: 'SECTION 4' },
              { key: 5, label: 'DECLARATION' }
            ].map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '20px',
                  border: activeSection === section.key ? 'none' : '2px solid #667eea',
                  backgroundColor: activeSection === section.key ? '#667eea' : 'transparent',
                  color: activeSection === section.key ? 'white' : '#667eea',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease'
                }}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container-custom" style={{ padding: '0 24px 80px' }}>
        <form onSubmit={handleSubmit}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            marginBottom: '30px'
          }}>
            {renderSection()}
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setActiveSection(Math.max(1, activeSection - 1))}
              disabled={activeSection === 1}
              style={{
                padding: '12px 24px',
                backgroundColor: activeSection === 1 ? '#e2e8f0' : '#667eea',
                color: activeSection === 1 ? '#718096' : 'white',
                border: 'none',
                borderRadius: '20px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: activeSection === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            {activeSection < 5 ? (
              <button
                type="button"
                onClick={() => setActiveSection(Math.min(5, activeSection + 1))}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                style={{
                  padding: '15px 40px',
                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '25px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Send size={20} />
                Submit Nomination
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AwardForm;